import { SandboxDomainError, SandboxStore } from '../../store';
import {
  SandboxShortcut,
  SandboxShortcutGroup,
  SandboxShortcutType,
} from '../../store/types';
import { SandboxOperationHandler, SandboxVariables } from '../types';
import { inputObject } from './utils';

const presetDeleteError =
  'Предустановленные сущности нельзя удалить в локальной песочнице';

const numberId = (value: unknown, entity: string): number => {
  const id = Number(value);
  if (!Number.isInteger(id) || id <= 0) {
    throw new SandboxDomainError(`${entity}: некорректный идентификатор`);
  }
  return id;
};

const titleValue = (value: unknown): string => {
  const title = String(value ?? '').trim();
  if (title.length < 2) {
    throw new SandboxDomainError('Название должно содержать минимум 2 символа');
  }
  return title;
};

const shortcutGroup = (
  store: SandboxStore,
  groupId: number,
  type?: SandboxShortcutType
): SandboxShortcutGroup => {
  const group = store
    .getState()
    .shortcutGroups.find((candidate) => candidate.id === groupId);
  if (!group) throw new SandboxDomainError('Группа приложений не найдена');
  if (type && group.groupType !== 'DEFAULT' && group.groupType !== type) {
    throw new SandboxDomainError('Тип группы не соответствует приложению');
  }
  return group;
};

const shortcutById = (
  store: SandboxStore,
  idValue: unknown
): SandboxShortcut => {
  const id = numberId(idValue, 'Приложение');
  const shortcut = store
    .getState()
    .shortcuts.find((candidate) => candidate.id === id);
  if (!shortcut) throw new SandboxDomainError('Приложение не найдено');
  return shortcut;
};

const publicShortcut = (
  store: SandboxStore,
  shortcut: SandboxShortcut
): Record<string, unknown> => ({
  ...shortcut,
  main_picture: shortcut.main_picture || shortcut.icon_path,
  group: shortcutGroup(store, shortcut.group_id),
});

const nestedStringArray = (value: unknown, fallback: string[]): string[] => {
  if (value === undefined) return fallback;
  const source = value as { value?: unknown } | null;
  const raw = Array.isArray(source) ? source : source?.value;
  if (!Array.isArray(raw)) return fallback;
  return raw
    .map(String)
    .map((item) => item.trim())
    .filter(Boolean);
};

const optionalString = (value: unknown, fallback: string): string =>
  value === undefined || value === null ? fallback : String(value);

const updateShortcutFields = (
  store: SandboxStore,
  shortcut: SandboxShortcut,
  input: Record<string, unknown>
): SandboxShortcut => {
  const groupId =
    input.group_id === undefined
      ? shortcut.group_id
      : numberId(input.group_id, 'Группа');
  shortcutGroup(store, groupId, shortcut.type);

  shortcut.title =
    input.title === undefined ? shortcut.title : titleValue(input.title);
  shortcut.group_id = groupId;
  shortcut.sort =
    input.sort === undefined ? shortcut.sort : Math.max(0, Number(input.sort));
  shortcut.path = nestedStringArray(input.paths, shortcut.path);
  shortcut.screenshots = nestedStringArray(
    input.screenshots,
    shortcut.screenshots
  );
  shortcut.work_dir =
    input.work_dir === undefined
      ? shortcut.work_dir
      : input.work_dir === null
      ? null
      : String(input.work_dir);
  shortcut.args = optionalString(input.args, shortcut.args);
  shortcut.description = optionalString(
    input.description,
    shortcut.description
  );
  shortcut.main_picture = optionalString(
    input.main_picture,
    shortcut.main_picture
  );
  shortcut.icon_path = optionalString(input.icon_path, shortcut.icon_path);
  shortcut.popular =
    input.popular === undefined ? shortcut.popular : Boolean(input.popular);
  shortcut.run_as_admin =
    input.run_as_admin === undefined
      ? shortcut.run_as_admin
      : Boolean(input.run_as_admin);
  shortcut.only_for_registered =
    input.only_for_registered === undefined
      ? shortcut.only_for_registered
      : Boolean(input.only_for_registered);
  shortcut.free_run =
    input.free_run === undefined ? shortcut.free_run : Boolean(input.free_run);
  shortcut.age_rating =
    input.age_rating === undefined || input.age_rating === null
      ? shortcut.age_rating
      : Math.max(0, Number(input.age_rating));
  shortcut.template_id =
    input.template_id === undefined
      ? shortcut.template_id
      : input.template_id === null
      ? undefined
      : numberId(input.template_id, 'Шаблон');

  if (input.game_account_group_id !== undefined) {
    shortcut.game_account_group = input.game_account_group_id
      ? {
          id: numberId(input.game_account_group_id, 'Группа аккаунтов'),
          title: 'Клубные аккаунты',
        }
      : null;
  }

  if (shortcut.type === 'APPLICATION' && shortcut.icon_path) {
    shortcut.main_picture = shortcut.icon_path;
  }
  return shortcut;
};

const sortShortcuts = (
  variables: SandboxVariables,
  store: SandboxStore
): boolean => {
  const input = variables.input;
  if (!Array.isArray(input)) {
    throw new SandboxDomainError('Не передан порядок приложений');
  }
  input.forEach((rawItem) => {
    const item = rawItem as Record<string, unknown>;
    const shortcut = shortcutById(store, item.id);
    shortcut.sort = Math.max(0, Number(item.sort));
  });
  return true;
};

const sortGroups = (
  variables: SandboxVariables,
  store: SandboxStore
): boolean => {
  const input = variables.input;
  if (!Array.isArray(input)) {
    throw new SandboxDomainError('Не передан порядок групп');
  }
  input.forEach((rawItem) => {
    const item = rawItem as Record<string, unknown>;
    const group = shortcutGroup(store, numberId(item.id, 'Группа'));
    group.sort = Number(item.sort);
  });
  return true;
};

export const applicationResolvers: Record<string, SandboxOperationHandler> = {
  shortcuts: (_, store) => ({
    shortcuts: store
      .getState()
      .shortcuts.map((shortcut) => publicShortcut(store, shortcut)),
  }),
  ShortcutGroups: (_, store) => ({
    shortcutGroups: store.getState().shortcutGroups,
  }),
  shortcutGroups: (_, store) => ({
    shortcutGroups: store.getState().shortcutGroups,
  }),
  shortcut: (variables, store) => ({
    shortcut: publicShortcut(store, shortcutById(store, variables.id)),
  }),
  createShortcut: (variables, store) => {
    const input = inputObject(variables);
    const type = String(input.type) as SandboxShortcutType;
    if (type !== 'GAME' && type !== 'APPLICATION') {
      throw new SandboxDomainError('Не выбран тип приложения');
    }
    const groupId = numberId(input.group_id, 'Группа');
    shortcutGroup(store, groupId, type);
    const state = store.getState();
    const shortcut: SandboxShortcut = {
      origin: 'runtime',
      id: state.nextShortcutId++,
      type,
      title: titleValue(input.title),
      group_id: groupId,
      sort: Number(input.sort ?? 0),
      popular: Boolean(input.popular ?? true),
      path: [],
      work_dir: null,
      args: '',
      main_picture: '',
      screenshots: [],
      description: '',
      icon_path: '',
      run_as_admin: false,
      only_for_registered: false,
      age_rating: null,
      free_run: true,
      game_account_group: null,
    };
    updateShortcutFields(store, shortcut, input);
    state.shortcuts.push(shortcut);
    return { createShortcut: publicShortcut(store, shortcut) };
  },
  updateShortcut: (variables, store) => {
    const shortcut = shortcutById(store, variables.id);
    updateShortcutFields(store, shortcut, inputObject(variables));
    return { updateShortcut: publicShortcut(store, shortcut) };
  },
  updateAllShortcut: (variables, store) => {
    const shortcut = shortcutById(store, variables.updateShortcutId);
    updateShortcutFields(
      store,
      shortcut,
      (variables.updateShortcutInput as Record<string, unknown>) ?? {}
    );
    sortShortcuts({ input: variables.updateShortcutSortInput }, store);
    return {
      updateShortcut: publicShortcut(store, shortcut),
      updateShortcutSort: true,
    };
  },
  updateShortcutSort: (variables, store) => ({
    updateShortcutSort: sortShortcuts(variables, store),
  }),
  deleteShortcut: (variables, store) => {
    const shortcut = shortcutById(store, variables.id);
    if (shortcut.origin === 'preset') {
      throw new SandboxDomainError(presetDeleteError);
    }
    store.getState().shortcuts = store
      .getState()
      .shortcuts.filter((candidate) => candidate.id !== shortcut.id);
    return { deleteShortcut: publicShortcut(store, shortcut) };
  },
  createShortcutGroup: (variables, store) => {
    const input = inputObject(variables);
    const groupType = String(input.groupType) as SandboxShortcutType;
    if (groupType !== 'GAME' && groupType !== 'APPLICATION') {
      throw new SandboxDomainError('Не выбран тип группы');
    }
    const state = store.getState();
    const group: SandboxShortcutGroup = {
      origin: 'runtime',
      id: state.nextShortcutGroupId++,
      sort: Number(input.sort ?? 0),
      title: titleValue(input.title),
      groupType,
    };
    state.shortcutGroups.push(group);
    return { createShortcutGroup: group };
  },
  UpdateShortcutGroup: (variables, store) => {
    const group = shortcutGroup(
      store,
      numberId(variables.updateShortcutGroupId, 'Группа')
    );
    const input = inputObject(variables);
    group.title = titleValue(input.title ?? group.title);
    group.sort = Number(input.sort ?? group.sort);
    return { updateShortcutGroup: group };
  },
  deleteShortcutGroup: (variables, store) => {
    const group = shortcutGroup(store, numberId(variables.id, 'Группа'));
    if (group.origin === 'preset') {
      throw new SandboxDomainError(presetDeleteError);
    }
    if (
      store
        .getState()
        .shortcuts.some((shortcut) => shortcut.group_id === group.id)
    ) {
      throw new SandboxDomainError(
        'Сначала перенеси приложения из удаляемой группы'
      );
    }
    store.getState().shortcutGroups = store
      .getState()
      .shortcutGroups.filter((candidate) => candidate.id !== group.id);
    return { deleteShortcutGroup: group };
  },
  updateShortcutGroupSort: (variables, store) => ({
    updateShortcutGroupSort: sortGroups(variables, store),
  }),
  ShortcutTemplatesV2: (_, store) => ({
    shortcutTemplates: store
      .getState()
      .shortcutTemplates.map(({ id, title, age_rating }) => ({
        id,
        title,
        age_rating,
      })),
  }),
  ShortcutTemplateV2: (variables, store) => {
    const id = numberId(variables.shortcutTemplateId, 'Шаблон');
    const template = store
      .getState()
      .shortcutTemplates.find((candidate) => candidate.id === id);
    if (!template) throw new SandboxDomainError('Шаблон не найден');
    return { shortcutTemplate: template };
  },
};
